/* eslint-env browser */

/**
 * Card Height Matcher Utility
 * Author: Alec Hewstone (Kubix Media)
 * Version: 1.0.0
 * Last Updated: 02/12/2025
 *
 * Purpose:
 * Provides uniform card heights in carousel/grid layouts by matching all cards
 * to the height of the tallest card. Designed for Horizon's resource-list pattern.
 *
 * Usage:
 * In a section .liquid file, add this script tag and initialize:
 *
 * <script src="{{ 'card-height-matcher.js' | asset_url }}" defer></script>
 * <script>
 *   document.addEventListener('DOMContentLoaded', () => {
 *     window.CardHeightMatcher.init('{{ section.id }}', '.your-card-selector');
 *   });
 * </script>
 *
 * Examples:
 * - Blog cards: init('{{ section.id }}', '.featured-blog-posts-card')
 * - Product cards: init('{{ section.id }}', '.product-card')
 * - Collection cards: init('{{ section.id }}', '.collection-card')
 *
 * Works with sections:
 * - featured-blog-posts.liquid
 * - product-list.liquid
 * - product-recommendations.liquid
 * - collection-list.liquid
 * - main-collection-list.liquid
 *
 * Typical implementation pattern:
 * 1. Add section setting for card_height_mode (fit/fill)
 * 2. Conditionally load this utility when mode == 'fill'
 * 3. Call init() with section ID and card selector
 */

(function () {
  // Store active matchers by section ID to prevent duplicates
  const activeMatchers = new Map();

  /**
   * Match card heights within a section.
   * @param {string} sectionId - Shopify section ID (e.g., from {{ section.id }})
   * @param {string} cardSelector - CSS selector for cards (e.g., '.product-card')
   */
  const matchHeights = (sectionId, cardSelector) => {
    const section = document.querySelector(`#shopify-section-${sectionId}`);
    if (!section) {
      // Section not found; nothing to do
      return;
    }

    const cards = section.querySelectorAll(cardSelector);
    if (!cards.length) {
      // No cards found for this selector in this section
      return;
    }

    // Reset to natural height for accurate measurement
    cards.forEach((card) => {
      // @ts-ignore - querySelectorAll returns Element[], but we know these are HTMLElements
      card.style.minHeight = 'auto';
    });

    // Measure after layout settles
    requestAnimationFrame(() => {
      let tallestHeight = 0;

      cards.forEach((card) => {
        // @ts-ignore - querySelectorAll returns Element[], but we know these are HTMLElements
        const height = card.offsetHeight;
        if (height > tallestHeight) {
          tallestHeight = height;
        }
      });

      if (!tallestHeight) return;

      // Apply tallest height to all cards
      cards.forEach((card) => {
        // @ts-ignore - querySelectorAll returns Element[], but we know these are HTMLElements
        card.style.minHeight = `${tallestHeight}px`;
      });
    });
  };

  /**
   * Initialize height matching for a section.
   * Sets up resize handling and stores matcher reference.
   * @param {string} sectionId - Shopify section ID
   * @param {string} cardSelector - CSS selector for cards
   */
  const init = (sectionId, cardSelector) => {
    // Prevent duplicate initialization
    if (activeMatchers.has(sectionId)) {
      return;
    }

    // Initial match
    matchHeights(sectionId, cardSelector);

    // Debounced resize handler
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let resizeTimer;
    const handleResize = () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        matchHeights(sectionId, cardSelector);
      }, 250);
    };

    window.addEventListener('resize', handleResize);

    // Theme editor support: re-run when section reloads
    // @ts-ignore - CustomEvent type varies, but we know the structure
    const handleSectionLoad = (event) => {
      const detail = event && event.detail;
      if (!detail || detail.sectionId !== sectionId) return;
      matchHeights(sectionId, cardSelector);
    };

    document.addEventListener('shopify:section:load', handleSectionLoad);

    // Store reference for cleanup
    activeMatchers.set(sectionId, {
      handleResize,
      handleSectionLoad,
    });
  };

  /**
   * Cleanup a specific matcher (useful for theme editor).
   * @param {string} sectionId - Shopify section ID
   */
  const destroy = (sectionId) => {
    const matcher = activeMatchers.get(sectionId);
    if (!matcher) return;

    window.removeEventListener('resize', matcher.handleResize);
    document.removeEventListener('shopify:section:load', matcher.handleSectionLoad);
    activeMatchers.delete(sectionId);
  };

  // Expose to global scope
  // @ts-ignore - Extending window object with custom property
  window.CardHeightMatcher = {
    init,
    destroy,
    matchHeights, // Exposed for manual triggering if needed
  };
})();
